/**
 * Shared LLM HTTP client for prioritization features.
 *
 * Transport order for all paths: Peak LLM → BYOK OpenAI → BYOK Anthropic.
 *
 * - **Workspace agents** (ATC, Twins, Scribe, Nexus): `callAgentLlm` — honors
 *   workspace model + system prompt; throws {@link AgentLlmError} when all paths fail.
 * - **Assists & narrators** (shaping, contestation, intelligence narration, …):
 *   `callFeatureLlm` — same routing as agents; returns `null` for graceful degradation.
 * - **Legacy**: `callLlmWithFallback` delegates to `callFeatureLlm`.
 *
 * Registry & taxonomy: `src/lib/ai/aiCapabilityRegistry.ts`
 * Canon: `DOCS_V4/00-canon/24-ai-capability-taxonomy-and-registry.v1.md`
 */

import {
  providerHasApiKey,
  resolveAIProvider,
  aiKeyPreview,
  getAnthropicKeySource,
  personalAiFetch,
} from './aiProviderKeys';
import { buildOpenAIChatCompletionBody } from './openaiChatCompletionBody';
import { DEFAULT_CLAUDE_MODEL, DEFAULT_MODEL, anthropicModelParams, currentModelFor } from '../../supabase/functions/_shared/modelRequest.ts';
import { resolveLlmChatSimpleUrl } from './envConfig';
import { getSupabaseClient } from './supabaseClient';

export { resolveLlmChatSimpleUrl, resolveLlmApiBaseUrl } from './envConfig';

/**
 * The signed-in person's token, for our own server AI routes. Those routes spend
 * the deployment's AI key and refuse a request with nobody signed in behind it.
 * Sent only to a same-origin address, never to another host.
 */
async function signInHeader(url: string): Promise<Record<string, string>> {
  if (!url.startsWith('/')) return {};
  try {
    const { data } = await getSupabaseClient().auth.getSession();
    const token = data.session?.access_token;
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

/** Thrown when an agent LLM call fails or settings cannot be honored via Peak LLM. */
export class AgentLlmError extends Error {
  readonly status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'AgentLlmError';
    this.status = status;
  }
}

export interface AgentLlmCallOptions {
  userPrompt: string;
  systemPrompt: string;
  model: string;
  conversationId: string;
  maxTokens?: number;
  temperature?: number;
  timeoutMs?: number;
}

/** Validate model is usable with Peak LLM (OpenAI models only). */
function isPeakLlmCompatibleModel(model: string): boolean {
  const lower = model.trim().toLowerCase();
  return lower.length > 0 && !lower.startsWith('claude') && !lower.startsWith('gemini');
}

/**
 * Agent LLM entry point.
 *
 * Call order:
 *   1. Peak LLM (when configured and model is OpenAI-compatible)
 *   2. BYOK OpenAI  (when Peak LLM is unavailable or model is not Peak-compatible)
 *   3. BYOK Anthropic (when OpenAI key is absent but Anthropic key is present)
 *
 * Throws {@link AgentLlmError} only when all three paths are unavailable.
 */
export async function callAgentLlm(options: AgentLlmCallOptions): Promise<string> {
  const systemPrompt = options.systemPrompt.trim();
  if (!systemPrompt) {
    throw new AgentLlmError('Agent system prompt is empty.');
  }
  const userPrompt = options.userPrompt.trim();
  if (!userPrompt) {
    throw new AgentLlmError('Agent prompt is empty.');
  }
  if (!options.model.trim()) {
    throw new AgentLlmError('Agent model is not configured. Set a model in Settings → Agents.');
  }

  // ── 1. Peak LLM ────────────────────────────────────────────────────────────
  // Why the deployment's route did not answer, kept for when no own key is left
  // to try — so a refused key says so, instead of "no AI provider".
  let peakError: AgentLlmError | undefined;
  const peakUrl = resolveLlmChatSimpleUrl();
  if (peakUrl) {
    const peakModel = isPeakLlmCompatibleModel(options.model)
      ? currentModelFor(options.model)
      : DEFAULT_MODEL;
    try {
      const response = await fetch(peakUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await signInHeader(peakUrl)) },
        body: JSON.stringify({
          userMessage: userPrompt,
          system: systemPrompt,
          model: peakModel,
          conversationId: options.conversationId,
          max_tokens: options.maxTokens,
          temperature: options.temperature,
        }),
        signal: options.timeoutMs ? AbortSignal.timeout(options.timeoutMs) : undefined,
      });

      let data: Record<string, unknown> = {};
      try { data = (await response.json()) as Record<string, unknown>; } catch { /* non-JSON */ }

      if (response.ok) {
        const text = String(data.content ?? data.message ?? data.response ?? '').trim();
        if (text) return text;
        peakError = new AgentLlmError('The AI service returned an empty answer.', response.status);
      } else {
        const reason = typeof data.error === 'string' && data.error.trim()
          ? data.error.trim()
          : `The AI service answered with error ${response.status}.`;
        peakError = new AgentLlmError(reason, response.status);
      }
      // Non-ok or empty → fall through to BYOK
    } catch {
      // Peak LLM unreachable → fall through to BYOK
      peakError = new AgentLlmError('The AI service could not be reached.');
    }
  }

  // ── 2. BYOK fallback — try preferred provider, then the other on billing/auth errors
  const errors: AgentLlmError[] = [];
  const preferred = resolveAIProvider();
  const chain: Array<'openai' | 'anthropic'> = [];

  if (preferred === 'anthropic') {
    if (providerHasApiKey('anthropic')) chain.push('anthropic');
    if (providerHasApiKey('openai')) chain.push('openai');
  } else {
    if (providerHasApiKey('openai')) chain.push('openai');
    if (providerHasApiKey('anthropic')) chain.push('anthropic');
  }

  for (const provider of chain) {
    try {
      if (provider === 'openai') {
        return await callOpenAIChat({
          model: isPeakLlmCompatibleModel(options.model) ? currentModelFor(options.model) : DEFAULT_MODEL,
          messages: [
            { role: 'system', content: systemPrompt },
            { role: 'user', content: userPrompt },
          ],
          temperature: options.temperature,
          max_tokens: options.maxTokens,
        });
      }
      const model = options.model.trim().toLowerCase().startsWith('claude')
        ? currentModelFor(options.model)
        : DEFAULT_CLAUDE_MODEL;
      return await callAnthropicChat({
        model,
        system: systemPrompt,
        userPrompt,
        temperature: options.temperature,
        max_tokens: options.maxTokens,
      });
    } catch (err) {
      const llmErr = err instanceof AgentLlmError
        ? err
        : new AgentLlmError(err instanceof Error ? err.message : 'LLM request failed');
      errors.push(llmErr);
      if (!isRecoverableProviderError(llmErr) || chain.indexOf(provider) === chain.length - 1) {
        throw llmErr;
      }
    }
  }

  throw errors[errors.length - 1] ?? peakError ?? new AgentLlmError(
    'No AI provider available. Configure Peak LLM or add an API key in Settings → Connectors.',
  );
}

/** Billing, auth, and rate-limit failures — try the other BYOK provider if configured. */
function isRecoverableProviderError(err: AgentLlmError): boolean {
  if (err.status && [401, 402, 403, 429].includes(err.status)) return true;
  const msg = err.message.toLowerCase();
  return (
    msg.includes('credit balance')
    || msg.includes('billing')
    || msg.includes('insufficient')
    || msg.includes('invalid x-api-key')
    || msg.includes('authentication')
    || msg.includes('rate limit')
  );
}

export async function callPeakLlmSimple(
  prompt: string,
  conversationId: string,
): Promise<string> {
  const peakUrl = resolveLlmChatSimpleUrl();
  const response = await fetch(peakUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await signInHeader(peakUrl)) },
    body: JSON.stringify({ content: prompt, conversationId }),
  });
  if (!response.ok) throw new Error(`Peak LLM error: ${response.status}`);
  const data = await response.json();
  return (data.content ?? data.message ?? '') as string;
}

export interface OpenAIChatMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

export async function callOpenAIChat(options: {
  messages: OpenAIChatMessage[];
  model?: string;
  temperature?: number;
  max_tokens?: number;
}): Promise<string> {
  if (!providerHasApiKey('openai')) throw new AgentLlmError('No OpenAI API key');

  // The person's own key, attached on the server (ai-proxy, migration 982).
  const response = await personalAiFetch(
    'openai',
    buildOpenAIChatCompletionBody({
      model: options.model ?? DEFAULT_MODEL,
      messages: options.messages,
      temperature: options.temperature ?? 0.3,
      maxTokens: options.max_tokens ?? 600,
    }),
  );

  let data: { choices?: { message?: { content?: string } }[]; error?: { message?: string } } = {};
  try {
    data = await response.json();
  } catch {
    /* non-JSON */
  }

  if (!response.ok) {
    const detail = data?.error?.message;
    throw new AgentLlmError(
      detail ? `OpenAI error: ${detail}` : `OpenAI error: ${response.status}`,
      response.status,
    );
  }
  const text = (data.choices?.[0]?.message?.content ?? '') as string;
  if (!text.trim()) throw new AgentLlmError('OpenAI returned an empty response');
  return text;
}

export async function callAnthropicChat(options: {
  system: string;
  userPrompt: string;
  model?: string;
  temperature?: number;
  max_tokens?: number;
}): Promise<string> {
  if (!providerHasApiKey('anthropic')) throw new AgentLlmError('No Anthropic API key');

  // The person's own key, attached on the server (ai-proxy, migration 982).
  const response = await personalAiFetch('anthropic', {
    model: currentModelFor(options.model ?? DEFAULT_CLAUDE_MODEL),
    system: options.system,
    messages: [{ role: 'user', content: options.userPrompt }],
    ...anthropicModelParams(currentModelFor(options.model ?? DEFAULT_CLAUDE_MODEL), {
      temperature: options.temperature ?? 0.3,
      maxTokens: options.max_tokens ?? 1024,
    }),
  });

  let data: { content?: { text?: string }[]; error?: { message?: string; type?: string } } = {};
  try {
    data = await response.json();
  } catch {
    /* non-JSON */
  }

  if (!response.ok) {
    const detail = data?.error?.message || data?.error?.type;
    let message = detail
      ? `Anthropic error: ${detail}`
      : `Anthropic error: ${response.status}`;

    if (detail?.toLowerCase().includes('credit balance') || detail?.toLowerCase().includes('billing')) {
      const preview = aiKeyPreview('anthropic');
      const source = getAnthropicKeySource();
      const sourceHint = source === 'env'
        ? ' (loaded from .env — may differ from the account you topped up)'
        : source === 'saved'
          ? ' (saved in Settings → Connectors)'
          : '';
      message += preview
        ? `${sourceHint}. Key ending ${preview} — try clearing and re-pasting your Anthropic API key in Settings → Connectors.`
        : '. Re-paste your Anthropic API key in Settings → Connectors.';
    }

    throw new AgentLlmError(message, response.status);
  }

  return (data.content?.[0]?.text ?? '') as string;
}

const DEFAULT_FEATURE_SYSTEM_PROMPT =
  'You help with prioritization. Respond with valid JSON only when asked.';

export interface FeatureLlmCallOptions {
  userPrompt: string;
  systemPrompt?: string;
  conversationId: string;
  model?: string;
  maxTokens?: number;
  temperature?: number;
  timeoutMs?: number;
}

/**
 * Non-agent LLM entry point — inline assists, narrators, embedded advisors.
 * Uses the same Peak → BYOK OpenAI → BYOK Anthropic routing as {@link callAgentLlm}
 * but returns `null` instead of throwing when no provider is reachable.
 */
export async function callFeatureLlm(
  options: FeatureLlmCallOptions,
): Promise<string | null> {
  const userPrompt = options.userPrompt.trim();
  if (!userPrompt) return null;

  try {
    return await callAgentLlm({
      userPrompt,
      systemPrompt:
        options.systemPrompt?.trim() || DEFAULT_FEATURE_SYSTEM_PROMPT,
      model: options.model?.trim() || DEFAULT_MODEL,
      conversationId: options.conversationId,
      maxTokens: options.maxTokens,
      temperature: options.temperature,
      timeoutMs: options.timeoutMs,
    });
  } catch {
    return null;
  }
}

/** @deprecated Prefer {@link callFeatureLlm}. Kept for intelligence narration callers. */
export async function callLlmWithFallback(options: {
  prompt: string;
  conversationId: string;
  systemPrompt?: string;
  model?: string;
}): Promise<string | null> {
  return callFeatureLlm({
    userPrompt: options.prompt,
    systemPrompt: options.systemPrompt,
    conversationId: options.conversationId,
    model: options.model,
  });
}
